import React from "react";
import {
  FiSettings,
  FiFileText,
  FiRepeat,
  FiLayers,
  FiDatabase,
  FiShield,
} from "react-icons/fi";
import { MdOutlineWork, MdOutlineAutorenew } from "react-icons/md";
import AIAutomationTemplate from "./AIAutomationTemplate";

const AIRPAAssistant = () => {
  const features = [
    {
      title: "RPA + AI Workflows",
      description:
        "Blend deterministic RPA bots with cognitive AI for exception handling, decisioning, and intelligent branching.",
      icon: <FiSettings />,
    },
    {
      title: "Automated Back-Office Tasks",
      description:
        "Digitize finance, HR, procurement, and IT workflows with unattended bots and resilient schedules.",
      icon: <MdOutlineWork />,
    },
    {
      title: "Document Understanding",
      description:
        "Ingest invoices, contracts, and forms using OCR + LLM extraction with validation loops.",
      icon: <FiFileText />,
    },
    {
      title: "Repetitive Task Automation",
      description:
        "Automate data entry, reconciliation, reporting, and notification tasks to reduce manual load.",
      icon: <FiRepeat />,
    },
    {
      title: "Bot Orchestration & Monitoring",
      description:
        "Centralized control rooms schedule bots, manage credentials, and surface SLA alerts.",
      icon: <FiLayers />,
    },
    {
      title: "Compliance & Security Controls",
      description:
        "Role-based access, encryption, and audit logging keep automations compliant across regulated industries.",
      icon: <FiShield />,
    },
  ];

  const steps = [
    {
      title: "Process Discovery",
      description:
        "Identify automation-ready processes, baseline effort, and define ROI targets with stakeholders.",
    },
    {
      title: "Solution Blueprinting",
      description:
        "Design hybrid RPA + AI architectures, covering integrations, data flows, and governance.",
    },
    {
      title: "Build & Validate",
      description:
        "Develop bots, train document models, and run UAT with business SMEs to ensure accuracy.",
    },
    {
      title: "Run & Optimize",
      description:
        "Deploy to production, monitor KPIs, and expand the automation backlog through continuous assessment.",
    },
  ];

  const benefits = [
    "Reduce operational costs by automating labor-heavy processes",
    "Improve accuracy with AI validation and human-in-the-loop review",
    "Accelerate cycle times for approvals, reconciliations, and reporting",
    "Increase workforce capacity for strategic work",
    "Maintain compliance with granular logging and controls",
    "Scale automations quickly using reusable components",
  ];

  const expertise = [
    "Certified UiPath, Automation Anywhere, Power Automate, and Robocorp engineers",
    "Pre-built cognitive skills for invoices, KYC, insurance claims, and logistics docs",
    "Integration accelerators for ERP, HRIS, banking, and legacy mainframes",
    "Managed control tower for monitoring, patching, and bot support",
    "Strong change management and training programs",
    "Security-first delivery with SOC 2, ISO 27001 aligned controls",
  ];

   const image =[
{
  side_image:"../assets/images/ai-automation/6.png",
}

  ];

  const useCases = [
    {
      industry: "Banking",
      title: "Loan Boarding Automation",
      description:
        "Bots ingest loan packages, validate data, and push records into core banking while notifying teams of exceptions.",
    },
    {
      industry: "Insurance",
      title: "Claims Intake Assistant",
      description:
        "AI extracts data from FNOL docs, triggers RPA workflows, and updates policy admin systems.",
    },
    {
      industry: "Healthcare",
      title: "Revenue Cycle Bot",
      description:
        "Automates eligibility checks, charge capture, and denial management with payer-specific rules.",
    },
    {
      industry: "Manufacturing",
      title: "Procure-to-Pay Automation",
      description:
        "Bots reconcile POs, GRNs, and invoices, and sync updates with SAP/Oracle ERPs.",
    },
  ];

  const reasons = [
    "End-to-end services from discovery to run operations",
    "Hybrid AI + RPA expertise ensuring resilient automations",
    "Library of accelerators to speed build cycles",
    "Outcome-based governance model tied to business KPIs",
    "Flexible delivery across onsite, nearshore, and offshore teams",
    "Continuous improvement pods measuring ROI and adoption",
  ];

  return (
    <AIAutomationTemplate
      title="AI RPA Assistant"
      heroDescription="Fuse robotics with AI cognition to streamline back-office operations, document processing, and repetitive work."
      about={{
        heading: "About AI RPA Assistant Services",
        description:
          "We modernize automation programs with AI-enhanced bots that understand documents, make decisions, and keep humans in control.",
      }}
      features={features}
      steps={steps}
      benefits={benefits}
      expertise={expertise}
      image={image}
      useCases={useCases}
      reasons={reasons}
      ctaLabel="Automate Back-Office Work"
      meta={{
        title: "AI RPA Assistant | Intelligent Automation Services | Ayanshtech",
        description:
          "RPA + AI workflows for back-office automation, document processing, and repetitive task elimination.",
        ogTitle: "AI RPA Assistant Solutions",
        ogDescription:
          "Combine RPA and AI to automate back-office tasks with document intelligence and orchestration.",
        ogUrl: "http://ayanshtechnology.com/ai-rpa-assistant",
        canonical: "http://ayanshtechnology.com/ai-rpa-assistant",
      }}
    />
  );
};

export default AIRPAAssistant;


